import { html, nothing } from "lit";
import type { GatewaySessionRow, SessionsListResult } from "../types.ts";
import { formatRelativeTimestamp } from "../format.ts";
import { pathForTab } from "../navigation.ts";
import { formatSessionTokens } from "../presenter.ts";
import { t } from "../i18n.ts";
import { extractText } from "../chat/message-extract.ts";

export type SessionsProps = {
  loading: boolean;
  result: SessionsListResult | null;
  error: string | null;
  activeMinutes: string;
  limit: string;
  includeGlobal: boolean;
  includeUnknown: boolean;
  basePath: string;
  expandedKey: string | null;
  expandedMessages: unknown[] | null;
  expandedLoading: boolean;
  onFiltersChange: (next: {
    activeMinutes: string;
    limit: string;
    includeGlobal: boolean;
    includeUnknown: boolean;
  }) => void;
  onRefresh: () => void;
  onPatch: (
    key: string,
    patch: {
      label?: string | null;
      thinkingLevel?: string | null;
      verboseLevel?: string | null;
      reasoningLevel?: string | null;
    },
  ) => void;
  onDelete: (key: string) => void;
  onToggleTaskLog: (key: string) => void;
};

const THINK_LEVELS = ["", "off", "minimal", "low", "medium", "high", "xhigh"] as const;
const BINARY_THINK_LEVELS = ["", "off", "on"] as const;
const VERBOSE_LEVELS = [
  { value: "", label: "inherit" },
  { value: "off", label: "off (explicit)" },
  { value: "on", label: "on" },
  { value: "full", label: "full" },
] as const;
const REASONING_LEVELS = ["", "off", "on", "stream"] as const;

function normalizeProviderId(provider?: string | null): string {
  if (!provider) {
    return "";
  }
  const normalized = provider.trim().toLowerCase();
  if (normalized === "z.ai" || normalized === "z-ai") {
    return "zai";
  }
  return normalized;
}

function isBinaryThinkingProvider(provider?: string | null): boolean {
  return normalizeProviderId(provider) === "zai";
}

function resolveThinkLevelOptions(provider?: string | null): readonly string[] {
  return isBinaryThinkingProvider(provider) ? BINARY_THINK_LEVELS : THINK_LEVELS;
}

function withCurrentOption(options: readonly string[], current: string): string[] {
  if (!current) {
    return [...options];
  }
  if (options.includes(current)) {
    return [...options];
  }
  return [...options, current];
}

function withCurrentLabeledOption(
  options: readonly { value: string; label: string }[],
  current: string,
): Array<{ value: string; label: string }> {
  if (!current) {
    return [...options];
  }
  if (options.some((option) => option.value === current)) {
    return [...options];
  }
  return [...options, { value: current, label: `${current} (custom)` }];
}

function resolveThinkLevelDisplay(value: string, isBinary: boolean): string {
  if (!isBinary) {
    return value;
  }
  if (!value || value === "off") {
    return value;
  }
  return "on";
}

function resolveThinkLevelPatchValue(value: string, isBinary: boolean): string | null {
  if (!value) {
    return null;
  }
  if (!isBinary) {
    return value;
  }
  if (value === "on") {
    return "low";
  }
  return value;
}

export function renderSessions(props: SessionsProps) {
  const rows = props.result?.sessions ?? [];
  return html`
    <section class="card">
      <div class="row" style="justify-content: space-between;">
        <div>
          <div class="card-title">${t("sessions.title")}</div>
          <div class="card-sub">${t("sessions.title_sub")}</div>
        </div>
        <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
          ${props.loading ? t("common.loading") : t("common.refresh")}
        </button>
      </div>

      <div class="filters" style="margin-top: 14px;">
        <label class="field">
          <span>${t("sessions.active_within")}</span>
          <input
            .value=${props.activeMinutes}
            @input=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: (e.target as HTMLInputElement).value,
                limit: props.limit,
                includeGlobal: props.includeGlobal,
                includeUnknown: props.includeUnknown,
              })}
          />
        </label>
        <label class="field">
          <span>${t("sessions.limit")}</span>
          <input
            .value=${props.limit}
            @input=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: (e.target as HTMLInputElement).value,
                includeGlobal: props.includeGlobal,
                includeUnknown: props.includeUnknown,
              })}
          />
        </label>
        <label class="field checkbox">
          <span>${t("sessions.include_global")}</span>
          <input
            type="checkbox"
            .checked=${props.includeGlobal}
            @change=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: props.limit,
                includeGlobal: (e.target as HTMLInputElement).checked,
                includeUnknown: props.includeUnknown,
              })}
          />
        </label>
        <label class="field checkbox">
          <span>${t("sessions.include_unknown")}</span>
          <input
            type="checkbox"
            .checked=${props.includeUnknown}
            @change=${(e: Event) =>
              props.onFiltersChange({
                activeMinutes: props.activeMinutes,
                limit: props.limit,
                includeGlobal: props.includeGlobal,
                includeUnknown: (e.target as HTMLInputElement).checked,
              })}
          />
        </label>
      </div>

      ${
        props.error
          ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
          : nothing
      }

      <div class="muted" style="margin-top: 12px;">
        ${props.result ? `${t("sessions.store")}: ${props.result.path}` : ""}
      </div>

      <div class="table" style="margin-top: 16px;">
        <div class="table-head">
          <div>Key</div>
          <div>Label</div>
          <div>Kind</div>
          <div>Updated</div>
          <div>Tokens</div>
          <div>Thinking</div>
          <div>Verbose</div>
          <div>Reasoning</div>
          <div>Actions</div>
        </div>
        ${
          rows.length === 0
            ? html`
                <div class="muted">No sessions found.</div>
              `
            : rows.map(
                (row) => html`
                ${renderRow(row, props.basePath, props.onPatch, props.onDelete, props.loading, props.onToggleTaskLog, props.expandedKey === row.key)}
                ${props.expandedKey === row.key ? renderTaskLogPanel(props.expandedMessages, props.expandedLoading) : nothing}
              `,
              )
        }
      </div>
    </section>
  `;
}

function renderRow(
  row: GatewaySessionRow,
  basePath: string,
  onPatch: SessionsProps["onPatch"],
  onDelete: SessionsProps["onDelete"],
  disabled: boolean,
  onToggleTaskLog: SessionsProps["onToggleTaskLog"],
  isExpanded: boolean,
) {
  const updated = row.updatedAt ? formatRelativeTimestamp(row.updatedAt) : "n/a";
  const rawThinking = row.thinkingLevel ?? "";
  const isBinaryThinking = isBinaryThinkingProvider(row.modelProvider);
  const thinking = resolveThinkLevelDisplay(rawThinking, isBinaryThinking);
  const thinkLevels = withCurrentOption(resolveThinkLevelOptions(row.modelProvider), thinking);
  const verbose = row.verboseLevel ?? "";
  const verboseLevels = withCurrentLabeledOption(VERBOSE_LEVELS, verbose);
  const reasoning = row.reasoningLevel ?? "";
  const reasoningLevels = withCurrentOption(REASONING_LEVELS, reasoning);
  const displayName =
    typeof row.displayName === "string" && row.displayName.trim().length > 0
      ? row.displayName.trim()
      : null;
  const label = typeof row.label === "string" ? row.label.trim() : "";
  const showDisplayName = Boolean(displayName && displayName !== row.key && displayName !== label);
  const canLink = row.kind !== "global";
  const chatUrl = canLink
    ? `${pathForTab("chat", basePath)}?session=${encodeURIComponent(row.key)}`
    : null;

  return html`
    <div class="table-row">
      <div class="mono session-key-cell">
        ${canLink ? html`<a href=${chatUrl} class="session-link">${row.key}</a>` : row.key}
        ${showDisplayName ? html`<span class="muted session-key-display-name">${displayName}</span>` : nothing}
      </div>
      <div>
        <input
          .value=${row.label ?? ""}
          ?disabled=${disabled}
          placeholder="(optional)"
          @change=${(e: Event) => {
            const value = (e.target as HTMLInputElement).value.trim();
            onPatch(row.key, { label: value || null });
          }}
        />
      </div>
      <div>${row.kind}</div>
      <div>${updated}</div>
      <div>${formatSessionTokens(row)}</div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, {
              thinkingLevel: resolveThinkLevelPatchValue(value, isBinaryThinking),
            });
          }}
        >
          ${thinkLevels.map(
            (level) =>
              html`<option value=${level} ?selected=${thinking === level}>
                ${level || t("sessions.inherit")}
              </option>`,
          )}
        </select>
      </div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, { verboseLevel: value || null });
          }}
        >
          ${verboseLevels.map(
            (level) =>
              html`<option value=${level.value} ?selected=${verbose === level.value}>
                ${level.label === "inherit" ? t("sessions.inherit") : level.label}
              </option>`,
          )}
        </select>
      </div>
      <div>
        <select
          ?disabled=${disabled}
          @change=${(e: Event) => {
            const value = (e.target as HTMLSelectElement).value;
            onPatch(row.key, { reasoningLevel: value || null });
          }}
        >
          ${reasoningLevels.map(
            (level) =>
              html`<option value=${level} ?selected=${reasoning === level}>
                ${level || t("sessions.inherit")}
              </option>`,
          )}
        </select>
      </div>
      <div style="display: flex; gap: 6px; align-items: center;">
        <button
          class="btn ${isExpanded ? "btn--primary" : ""}"
          style="font-size: 12px; padding: 4px 10px;"
          @click=${() => onToggleTaskLog(row.key)}
          title=${t("sessions.task_log")}
        >
          ${t("sessions.task_log")}
        </button>
        <button class="btn danger" ?disabled=${disabled} @click=${() => onDelete(row.key)}>
          ${t("common.delete")}
        </button>
      </div>
    </div>
  `;
}

function extractToolCalls(message: unknown): Array<{ name: string; input?: string }> {
  const m = message as Record<string, unknown>;
  const content = m.content;
  if (!Array.isArray(content)) {
    return [];
  }
  return content
    .filter((p) => {
      const item = p as Record<string, unknown>;
      return item.type === "tool_use" || item.type === "function";
    })
    .map((p) => {
      const item = p as Record<string, unknown>;
      const name = (item.name as string) ?? "unknown";
      const input = item.input ? JSON.stringify(item.input).slice(0, 200) : undefined;
      return { name, input };
    });
}

function renderTaskLogPanel(messages: unknown[] | null, loading: boolean) {
  if (loading) {
    return html`
      <div class="table-row" style="grid-column: 1 / -1;">
        <div style="
          padding: 20px;
          text-align: center;
          color: var(--text-muted, #999);
          font-size: 13px;
          background: rgba(0,0,0,0.15);
          border-radius: 8px;
          margin: 4px 0;
        ">${t("common.loading")}</div>
      </div>
    `;
  }

  if (!messages || messages.length === 0) {
    return html`
      <div class="table-row" style="grid-column: 1 / -1;">
        <div style="
          padding: 20px;
          text-align: center;
          color: var(--text-muted, #999);
          font-size: 13px;
          background: rgba(0,0,0,0.15);
          border-radius: 8px;
          margin: 4px 0;
        ">${t("sessions.no_messages")}</div>
      </div>
    `;
  }

  return html`
    <div class="table-row" style="grid-column: 1 / -1;">
      <div style="
        background: rgba(0,0,0,0.15);
        border: 1px solid rgba(255,255,255,0.06);
        border-radius: 10px;
        margin: 4px 0 8px;
        padding: 16px;
        max-height: 500px;
        overflow-y: auto;
      ">
        <div style="font-size: 13px; font-weight: 600; margin-bottom: 12px; color: var(--text, #eee);">
          ${t("sessions.task_log")} (${messages.length} ${t("sessions.messages_label")})
        </div>
        <div style="display: flex; flex-direction: column; gap: 6px;">
          ${messages.map((msg) => {
            const m = msg as Record<string, unknown>;
            const role = typeof m.role === "string" ? m.role : "unknown";
            const text = extractText(msg);
            const toolCalls = extractToolCalls(msg);
            const isUser = role === "user";
            const isAssistant = role === "assistant";
            const isTool = role === "tool";

            const roleColors: Record<string, string> = {
              user: "#6c8cff",
              assistant: "#34d399",
              tool: "#f59e0b",
              system: "#a78bfa",
            };
            const roleColor = roleColors[role] ?? "#94a3b8";
            const roleLabel = isUser ? "User" : isAssistant ? "Assistant" : isTool ? "Tool" : role;
            const truncatedText = text && text.length > 500 ? text.slice(0, 500) + "…" : text;

            return html`
              <div style="
                padding: 10px 14px;
                border-radius: 8px;
                background: ${isUser ? "rgba(108,140,255,0.06)" : isAssistant ? "rgba(52,211,153,0.06)" : "rgba(255,255,255,0.03)"};
                border-left: 3px solid ${roleColor};
              ">
                <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                  <span style="
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    color: ${roleColor};
                  ">${roleLabel}</span>
                  ${
                    toolCalls.length > 0
                      ? html`<span style="font-size: 11px; color: var(--text-muted, #999);">${toolCalls.length} tool call${toolCalls.length > 1 ? "s" : ""}</span>`
                      : nothing
                  }
                </div>
                ${
                  truncatedText
                    ? html`<div style="
                      font-size: 13px;
                      line-height: 1.5;
                      color: var(--text, #ddd);
                      white-space: pre-wrap;
                      word-break: break-word;
                    ">${truncatedText}</div>`
                    : nothing
                }
                ${
                  toolCalls.length > 0
                    ? html`
                    <div style="margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px;">
                      ${toolCalls.map(
                        (tc) => html`
                          <span style="
                            font-size: 11px;
                            padding: 2px 8px;
                            border-radius: 6px;
                            background: rgba(245,158,11,0.1);
                            color: #f59e0b;
                            font-family: var(--font-mono, monospace);
                          " title=${tc.input ?? ""}>${tc.name}</span>
                        `,
                      )}
                    </div>
                  `
                    : nothing
                }
              </div>
            `;
          })}
        </div>
      </div>
    </div>
  `;
}
